import { create } from 'zustand';
import { get } from 'lodash';
import { devtools } from 'zustand/middleware';
import {
  ADMIN_ROLE_NAME,
  STAFF_ROLE_NAME,
  API_MANAGEMENT_PERMISSION,
  NEXA_PERMISSION,
} from '@src/utils/constants';

// this store can eventually replace redux store for managing profile

interface Profile {
  username: string;
  api_key: string;
  mini_odp_access: boolean;
  profile_id: number;
  primary: boolean;
  can_edit_policies: number;
  activation_creator_enabled: number;
  tzCode: string;
  role: string;
  tzName: string;
  timezone: number;
  hidden: boolean;
  customer_id: number;
  customer_name: string;
  nexa_permission: number | null;
  can_manage_nexa: boolean;
  api_management_permission: number | null;
}

export const useProfileStore = create<Profile>()(
  devtools((set) => ({}) as any, { name: 'profileStore' }),
);

export const initProfileStore = (dbState: Profile[]) => {
  const profile = get(dbState, 0, {});
  useProfileStore.setState((state) => ({ ...state, ...profile }));
};

// without subscription
export const getProfile = (): Profile => {
  const profile = useProfileStore.getState();
  return profile;
};

// with subscription
// not necessary since profile content does not change
export const useProfile = (): Profile => {
  const profile = useProfileStore((state) => state);
  return profile;
};

export const checkIsInternalUser = (): boolean => {
  const profile = getProfile();
  const username = profile.username || '';
  return (
    get(profile, 'hidden', false) &&
    (username.endsWith('@conviva.com') || username.endsWith('@conviva.ai'))
  );
};

export const getCustomerInfo = (): { customerId: number; customerName: string } => {
  const profile: Profile = getProfile();
  return {
    customerId: profile.customer_id,
    customerName: profile.customer_name,
  };
};

export const checkIsAdminUser = (): boolean => {
  const profile = getProfile();
  return get(profile, 'role', '') === ADMIN_ROLE_NAME;
};

export const checkCanEnableNexa = (): boolean => {
  const profile = getProfile();
  return profile.nexa_permission === NEXA_PERMISSION.ADMIN && profile.can_manage_nexa;
};

export const checkCanUseNexa = (): boolean => {
  const profile = getProfile();
  return (
    profile.nexa_permission === NEXA_PERMISSION.USAGE ||
    profile.nexa_permission === NEXA_PERMISSION.ADMIN
  );
};

export const checkCanViewNexaUsage = (): boolean => {
  const profile = getProfile();
  const isAdminOrStaff = profile.role === ADMIN_ROLE_NAME || profile.role === STAFF_ROLE_NAME;
  return isAdminOrStaff && checkCanUseNexa();
};

export const checkCanViewApiManagement = (): boolean => {
  const profile = getProfile();
  return (
    profile.api_management_permission === API_MANAGEMENT_PERMISSION.VIEW ||
    profile.api_management_permission === API_MANAGEMENT_PERMISSION.EDIT
  );
};

export const checkCanEditApiManagement = (): boolean => {
  const profile = getProfile();
  return profile.api_management_permission === API_MANAGEMENT_PERMISSION.EDIT;
};
